import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '../../../shared/api/errorText'
import { PartialBulkError, setCustomsStatusInChunks } from '../customsBulk'
import { pluralPositions } from '../customsSelection'

const NOTE_MAX = 1000

interface Props {
  itemIds: string[]
  onClose: () => void
  onSaved: (result: { updated: number; skipped: number }) => void
}

/**
 * «Нужна информация»: логист пишет, что нужно от менеджера (фото маркировки,
 * даташит, назначение товара…). Текст уходит менеджеру в уведомлении и виден
 * в подсказке статуса позиции.
 */
export function CustomsStatusModal({ itemIds: initialIds, onClose, onSaved }: Props) {
  const qc = useQueryClient()
  // После частичного сбоя повторяем только оставшиеся позиции.
  const [itemIds, setItemIds] = useState(initialIds)
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, saving])

  const canSubmit = !!note.trim() && !saving

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    setError('')
    try {
      // Больше 2000 позиций сервер за раз не примет — уходит порциями.
      const res = await setCustomsStatusInChunks(itemIds, 'NEEDS_INFO', note.trim())
      onSaved(res)
    } catch (e) {
      if (e instanceof PartialBulkError) {
        setItemIds(e.remainingIds)
        // Часть позиций уже сменила статус — очередь должна это показать.
        void qc.invalidateQueries({ queryKey: ['customs'] })
      }
      setError(apiErrorText(e, 'Не удалось сменить статус'))
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Нужна информация</h3>
          <button
            onClick={onClose}
            disabled={saving}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            {pluralPositions(itemIds.length)} получат статус «Нужна информация», менеджеры —
            уведомление с этим текстом.
          </p>
          <label className="block text-xs text-gray-500 mb-1" htmlFor="customs-needs-info">
            Что нужно от менеджера <span className="text-red-500">*</span>
          </label>
          <textarea
            id="customs-needs-info"
            autoFocus
            value={note}
            maxLength={NOTE_MAX}
            rows={4}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                void submit()
              }
            }}
            placeholder="Например: пришлите фото маркировки и назначение товара"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y min-h-[100px]"
          />
          <div className="text-right text-[11px] text-gray-400">
            {note.length}/{NOTE_MAX}
          </div>
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Сохранение…' : 'Отправить менеджеру'}
          </button>
        </div>
      </div>
    </div>
  )
}
